import { useState } from "react";
import { ArrowUpRight, Instagram, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { MaskTitle, Reveal, SectionLabel } from "./motion";
import { api, formatApiError } from "@/lib/api";
import { DRIVER, INSTAGRAM_URL } from "@/lib/content";
import { useLang } from "@/context/LangContext";

const TYPES = ["sponsor", "press", "fan", "other"];
const EMPTY = { name: "", email: "", company: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="label-mono text-white">{label}</span>
    {children}
  </label>
);

const inputCls = "mt-2 w-full border-0 border-b border-white/20 bg-transparent px-0 py-3 text-lg text-white placeholder:text-zinc-500 focus:border-volt focus:outline-none focus:ring-0 transition-colors";

export const Contact = ({ type, setType }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { t, lang } = useLang();
  const c = t.contact;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/messages", { ...form, type });
      setSent(true);
      setForm(EMPTY);
      toast.success(c.toast);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="bg-ink px-5 py-24 md:px-10 md:py-36">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 md:grid-cols-12">
        <div className="md:col-span-5">
          <SectionLabel index="07">{c.label}</SectionLabel>
          <MaskTitle key={lang} lines={[c.title[0], { text: c.title[1], className: "text-volt" }]}
            className="mt-6 font-display text-6xl font-black uppercase leading-[0.9] tracking-[0.03em] md:text-8xl" />
          <p className="mt-8 max-w-sm text-sm leading-relaxed text-zinc-400 md:text-base">
            {c.intro}
          </p>
          <div className="mt-10 space-y-4">
            <a href={`mailto:${DRIVER.email}`} data-testid="contact-email-link" className="group flex items-center gap-3 break-all text-white hover:text-volt">
              <Mail className="h-5 w-5 shrink-0 text-volt" /><span className="font-mono text-sm">{DRIVER.email}</span>
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="group flex items-center gap-3 text-white hover:text-volt">
              <Instagram className="h-5 w-5 text-volt" /><span className="font-mono text-sm">@{DRIVER.instagram}</span>
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </a>
          </div>
        </div>

        <Reveal className="md:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="border border-white/10 bg-surface p-6 md:p-10">
            <p className="label-mono text-white">{c.reason}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {TYPES.map((k) => (
                <button type="button" key={k} onClick={() => setType(k)} data-testid={`contact-type-${k}`}
                  className={`rounded-full px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${type === k ? "bg-trident text-white" : "border border-white/15 text-zinc-300 hover:border-white/40"}`}>
                  {c.types[k]}
                </button>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
              <Field label={c.name}><input required minLength={2} value={form.name} onChange={set("name")} placeholder={c.name_ph} className={inputCls} data-testid="contact-name-input" /></Field>
              <Field label={c.email}><input required type="email" value={form.email} onChange={set("email")} placeholder="nome@email.com" className={inputCls} data-testid="contact-email-input" /></Field>
            </div>
            <div className="mt-8">
              <Field label={c.company}><input value={form.company} onChange={set("company")} placeholder={c.company_ph} className={inputCls} data-testid="contact-company-input" /></Field>
            </div>
            <div className="mt-8">
              <Field label={c.message}><textarea required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder={c.message_ph} className={`${inputCls} resize-none`} data-testid="contact-message-input" /></Field>
            </div>
            <div className="mt-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              {sent ? <p className="label-mono text-volt" data-testid="contact-success">{c.success}</p> : <span className="label-mono text-white/80">{c.required}</span>}
              <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                className="group flex items-center gap-3 rounded-full bg-trident px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.04] disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {c.send}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
